import { tr, useArkmeLocale } from './locale.js'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type FormEvent, type ReactNode } from 'react'
import { CaretRight } from '@phosphor-icons/react/CaretRight'
import { CircleNotch } from '@phosphor-icons/react/CircleNotch'
import { Copy } from '@phosphor-icons/react/Copy'
import { IdentificationCard } from '@phosphor-icons/react/IdentificationCard'
import { Phone } from '@phosphor-icons/react/Phone'
import { WarningCircle } from '@phosphor-icons/react/WarningCircle'
import { WechatLogo } from '@phosphor-icons/react/WechatLogo'
import { AppleLogo } from '@phosphor-icons/react/AppleLogo'
import { GoogleLogo } from '@phosphor-icons/react/GoogleLogo'
import { DeviceMobile } from '@phosphor-icons/react/DeviceMobile'
import { EnvelopeSimple } from '@phosphor-icons/react/EnvelopeSimple'
import { X } from '@phosphor-icons/react/X'
import qrcode from 'qrcode-generator'
import pluginManifest from '../../package.json' with { type: 'json' }
import type {
  ArkmeAuthSnapshot,
  ArkmeBackgroundSoundEligibilityReason,
  ArkmeBackgroundSoundPreference,
  ArkmeClientConfig,
  ArkmeIdAvailabilitySnapshot,
  ArkmeIdMutationResult,
  ArkmeProviderCapabilities,
  ArkmeUserProfile,
  ArkmeUserProfileSnapshot,
} from '../types.js'
import { callArkme } from './api.js'
import { ArkmeProfileEditor } from './ArkmeProfileEditor.js'
import { ArkmeAboutProduct, ArkmeAboutDetails } from './ArkmeAboutDetails.js'
import { arkmeAppUpdateStore, type ArkmeAppUpdateSnapshot } from './app-update-store.js'
import { ArkmeUserAvatar } from './ArkmeAvatar.js'
import { arkmeAuthStore } from './auth-store.js'
import {
  arkmeDesktopNotifications,
  type ArkmeDesktopNotificationPermission,
} from './desktop-notification-runtime.js'
import { clearLastNavigationCache } from './navigation-cache.js'
import { retryArkmeRead } from './read-retry.js'
import {
  applyArkmeBackgroundSoundOwnerSnapshot,
  arkmeBackgroundSoundCaptureEnabled,
  arkmeBackgroundSoundServerPreferenceRuntime,
  arkmeMicrophonePermissionState,
  setArkmeBackgroundSoundFromSettings,
  subscribeArkmeBackgroundSoundCapturePreference,
  type ArkmeMicrophonePermissionState,
} from './record-input-capture.js'
import { arkmeUi } from './ui-controller.js'
import { verifyPhoneCaptcha } from './geetest.js'

export type ArkmeBackgroundSoundEligibilityStatus = 'loading' | ArkmeBackgroundSoundEligibilityReason
export type ArkmeBackgroundSoundCapabilityStatus = 'loading' | 'supported' | 'unsupported'

export function resolveArkmeBackgroundSoundEligibility(
  preference: Pick<ArkmeBackgroundSoundPreference, 'eligible' | 'eligibilityReason'> | undefined,
): ArkmeBackgroundSoundEligibilityReason {
  if (preference?.eligible === true && preference.eligibilityReason === 'eligible') return 'eligible'
  return preference?.eligibilityReason === 'membership-required'
    ? 'membership-required'
    : 'membership-unavailable'
}

export function describeArkmeBackgroundSoundSetting(input: {
  capability: ArkmeBackgroundSoundCapabilityStatus
  eligibility: ArkmeBackgroundSoundEligibilityStatus
  enabled: boolean
  permission: ArkmeMicrophonePermissionState
}): string {
  if (input.capability === 'loading') return tr("正在确认当前 Arkme Host 是否支持文字背景音…")
  if (input.capability === 'unsupported') return tr("当前 Arkme Host 不支持文字背景音，请升级后重试")
  if (input.eligibility === 'loading') return tr("正在确认当前账号的背景音会员权益…")
  if (input.eligibility === 'membership-required') return tr("免费版暂不支持背景音")
  if (input.eligibility === 'membership-unavailable') return tr("暂时无法确认会员权益")
  if (input.enabled && input.permission === 'granted') return tr("已开启，输入时自动记录环境背景音并随本条快记保存")
  if (input.enabled) {
    if (input.permission === 'denied') return tr("已开启；输入时检测到麦克风权限已拒绝，请在浏览器站点设置中允许")
    if (input.permission === 'unavailable') return tr("账号开关已开启，但当前浏览器不支持麦克风录音")
    return tr("已开启，首次输入时会请求麦克风权限")
  }
  if (input.permission === 'denied') return tr("未开启，麦克风权限已拒绝")
  if (input.permission === 'unavailable') return tr("当前浏览器不支持麦克风录音")
  return tr("未开启；开启后会在首次输入时请求麦克风权限")
}

interface SettingsRowProps {
  title: string
  description: string
  href?: string
  onClick?: () => void
  danger?: boolean
  disabled?: boolean
}

function SettingsRow({ title, description, href, onClick, danger = false, disabled = false }: SettingsRowProps) {
  const interactive = href !== undefined || onClick !== undefined
  const body: ReactNode = <>
    <strong className={danger ? 'is-danger' : ''}>{title}</strong>
    <span className="arkme-redesign-setting-summary">{description}</span>
    {interactive ? <CaretRight size={15} aria-hidden /> : <span className="arkme-redesign-trailing-slot" aria-hidden />}
  </>

  if (href !== undefined) {
    return <a className="arkme-redesign-setting-row" href={href} target="_blank" rel="noreferrer">{body}</a>
  }
  if (onClick !== undefined) {
    return <button data-arkme-feedback="neutral" type="button" className="arkme-redesign-setting-row" disabled={disabled} onClick={onClick}>{body}</button>
  }
  return <div className="arkme-redesign-setting-row">{body}</div>
}

export function BackgroundSoundSettingsRow({
  checked,
  busy,
  disabled = false,
  description,
  onChange,
}: {
  checked: boolean
  busy: boolean
  disabled?: boolean
  description: string
  onChange: (checked: boolean) => void
}) {
  const switchDisabled = busy || disabled
  return <div className="arkme-redesign-setting-row">
    <strong>{tr("文字背景音")}</strong>
    <span style={{ minWidth: 0, maxWidth: 430, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12 }}>
      <span className="arkme-redesign-setting-summary" style={{ minWidth: 0 }}>{description}</span>
      <button data-arkme-feedback="neutral"
        type="button"
        role="switch"
        aria-label={tr("文字背景音")}
        aria-checked={checked}
        aria-busy={busy}
        disabled={switchDisabled}
        onClick={() => { onChange(!checked) }}
        style={{
          position: 'relative',
          width: 40,
          height: 23,
          flex: '0 0 40px',
          padding: 0,
          border: 0,
          borderRadius: 999,
          background: checked ? '#00b96b' : '#d7d9dd',
          cursor: switchDisabled ? 'default' : 'pointer',
          opacity: switchDisabled ? .55 : 1,
          transition: 'background .16s ease',
        }}
      >
        <span aria-hidden style={{
          position: 'absolute',
          top: 3,
          left: 3,
          width: 17,
          height: 17,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 3px rgba(20, 24, 32, .22)',
          transform: checked ? 'translateX(17px)' : 'translateX(0)',
          transition: 'transform .16s ease',
        }} />
      </button>
    </span>
    <span className="arkme-redesign-trailing-slot" aria-hidden />
  </div>
}

interface VersionSettingsRowProps {
  title: string
  version: string
  feedback?: string
  actionLabel?: string
  onAction?: () => void
}

export function VersionSettingsRow({
  title,
  version,
  feedback,
  actionLabel,
  onAction,
}: VersionSettingsRowProps) {
  const hasAction = actionLabel !== undefined && onAction !== undefined
  return <div className={`arkme-redesign-setting-row arkme-redesign-version-row${hasAction ? '' : ' is-without-action'}`}>
    <span className="arkme-redesign-version-copy">
      <strong>{title}</strong>
      {feedback === undefined ? null : <small>{feedback}</small>}
    </span>
    {hasAction ?
      <span className="arkme-redesign-version-action-slot">
      <button data-arkme-feedback="neutral"
        type="button"
        className="arkme-redesign-update-button"
        aria-label={`${actionLabel}：${title}`}
        onClick={onAction}
      >
        {actionLabel}
      </button>
      </span> : null}
    <span className="arkme-redesign-version-value">{version}</span>
    <span className="arkme-redesign-trailing-slot" aria-hidden />
  </div>
}

function SettingsGroup({ title, children, id }: { title?: string; children: ReactNode; id?: string }) {
  return <section className="arkme-redesign-settings-group" {...(id === undefined ? {} : { id })}>
    {title !== undefined && <h2>{title}</h2>}
    <div>{children}</div>
  </section>
}

interface AccountInfoRowProps {
  icon: ReactNode
  title: string
  value: string
  action?: string
  disabled?: boolean
  onClick?: (() => void) | undefined
}

function AccountInfoRow({ icon, title, value, action, disabled = false, onClick }: AccountInfoRowProps) {
  const content = <>
    <span className="arkme-account-info-icon" aria-hidden>{icon}</span>
    <span className="arkme-account-info-copy">
      <strong>{title}</strong>
      <small>{value}</small>
    </span>
    <span className="arkme-account-info-action">{action}</span>
    {onClick === undefined ? <span className="arkme-redesign-trailing-slot" aria-hidden /> : <CaretRight size={15} aria-hidden />}
  </>
  if (onClick === undefined) return <div className="arkme-account-info-row">{content}</div>
  return <button data-arkme-feedback="neutral" type="button" className="arkme-account-info-row" disabled={disabled} onClick={onClick}>{content}</button>
}

export function WechatBindingSettingsRow({ bound, nickname, onBind, busy = false }: {
  bound: boolean | undefined
  nickname?: string | undefined
  // Only an actual authorization adapter may supply this capability.
  onBind?: (() => void) | undefined
  busy?: boolean
}) {
  const canBind = bound === false && onBind !== undefined
  return <AccountInfoRow
    icon={<WechatLogo size={18} />}
    title={tr("微信号")}
    value={bound === undefined ? tr('正在读取…') : bound ? nickname?.trim() || tr('已绑定') : tr('未绑定')}
    {...(canBind ? { action: tr(busy ? '绑定中…' : '绑定') } : {})}
    disabled={busy}
    onClick={canBind && !busy ? onBind : undefined}
  />
}

function WorldShareQrIcon({ size = 20 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M2 16.9C2 15.5906 2 14.9359 2.29472 14.455C2.45963 14.1859 2.68589 13.9596 2.955 13.7947C3.43594 13.5 4.09063 13.5 5.4 13.5H6.5C8.38562 13.5 9.32843 13.5 9.91421 14.0858C10.5 14.6716 10.5 15.6144 10.5 17.5V18.6C10.5 19.9094 10.5 20.5641 10.2053 21.045C10.0404 21.3141 9.81411 21.5404 9.545 21.7053C9.06406 22 8.40937 22 7.1 22C5.13594 22 4.15391 22 3.4325 21.5579C3.02884 21.3106 2.68945 20.9712 2.44208 20.5675" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M22 7.1C22 8.40937 22 9.06406 21.7053 9.545C21.5404 9.81411 21.3141 10.0404 21.045 10.2053C20.5641 10.5 19.9094 10.5 18.6 10.5H17.5C15.6144 10.5 14.6716 10.5 14.0858 9.91421C13.5 9.32843 13.5 8.38562 13.5 6.5V5.4C13.5 4.09063 13.5 3.43594 13.7947 2.955C13.9596 2.68589 14.1859 2.45963 14.455 2.29472C14.9359 2 15.5906 2 16.9 2C18.8641 2 19.8461 2 20.5675 2.44208C20.9712 2.68945 21.3106 3.02884 21.5579 3.4325" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M16.5 6.25C16.5 5.73459 16.5 5.47689 16.6291 5.29493C16.6747 5.23072 16.7307 5.17466 16.7949 5.12911C16.9769 5 17.2346 5 17.75 5C18.2654 5 18.5231 5 18.7051 5.12911C18.7693 5.17466 18.8253 5.23072 18.8709 5.29493C19 5.47689 19 5.73459 19 6.25C19 6.76541 19 7.02311 18.8709 7.20507C18.8253 7.26928 18.7693 7.32534 18.7051 7.37089C18.5231 7.5 18.2654 7.5 17.75 7.5C17.2346 7.5 16.9769 7.5 16.7949 7.37089C16.7307 7.32534 16.6747 7.26928 16.6291 7.20507C16.5 7.02311 16.5 6.76541 16.5 6.25Z" fill="currentColor" />
    <path d="M12.75 22C12.75 22.4142 13.0858 22.75 13.5 22.75C13.9142 22.75 14.25 22.4142 14.25 22H12.75ZM14.3889 13.8371L14.8055 14.4607L14.3889 13.8371ZM13.8371 14.3889L13.2135 13.9722L13.8371 14.3889ZM19 12.75H17V14.25H19V12.75ZM12.75 19V22H14.25V19H12.75ZM17 12.75C16.3134 12.75 15.742 12.7491 15.281 12.796C14.8075 12.8441 14.3682 12.9489 13.9722 13.2135L14.8055 14.4607C14.914 14.3882 15.078 14.3244 15.4328 14.2883C15.8002 14.2509 16.2822 14.25 17 14.25V12.75ZM14.25 17C14.25 16.2822 14.2509 15.8002 14.2883 15.4328C14.3244 15.078 14.3882 14.914 14.4607 14.8055L13.2135 13.9722C12.9489 14.3682 12.8441 14.8075 12.796 15.281C12.7491 15.742 12.75 16.3134 12.75 17H14.25ZM13.9722 13.2135C13.6719 13.4141 13.4141 13.6719 13.2135 13.9722L14.4607 14.8055C14.5519 14.669 14.669 14.5519 14.8055 14.4607L13.9722 13.2135Z" fill="currentColor" />
    <path d="M22.75 13.5C22.75 13.0858 22.4142 12.75 22 12.75C21.5858 12.75 21.25 13.0858 21.25 13.5H22.75ZM20.7654 21.8478L21.0524 22.5407L20.7654 21.8478ZM21.8478 20.7654L21.1548 20.4784V20.4784L21.8478 20.7654ZM17 22.75H19V21.25H17V22.75ZM22.75 17V13.5H21.25V17H22.75ZM19 22.75C19.4557 22.75 19.835 22.7504 20.1454 22.7292C20.4625 22.7076 20.762 22.661 21.0524 22.5407L20.4784 21.1548C20.4012 21.1868 20.284 21.2163 20.0433 21.2327C19.7958 21.2496 19.4762 21.25 19 21.25V22.75ZM21.25 19C21.25 19.4762 21.2496 19.7958 21.2327 20.0433C21.2163 20.284 21.1868 20.4012 21.1548 20.4784L22.5407 21.0524C22.661 20.762 22.7076 20.4625 22.7292 20.1454C22.7504 19.835 22.75 19.4557 22.75 19H21.25ZM21.0524 22.5407C21.7262 22.2616 22.2616 21.7262 22.5407 21.0524L21.1548 20.4784C21.028 20.7846 20.7846 21.028 20.4784 21.1549L21.0524 22.5407Z" fill="currentColor" />
    <path d="M2 7.1C2 5.13594 2 4.15391 2.44208 3.4325C2.68945 3.02884 3.02884 2.68945 3.4325 2.44208C4.15391 2 5.13594 2 7.1 2C8.40937 2 9.06406 2 9.545 2.29472C9.81411 2.45963 10.0404 2.68589 10.2053 2.955C10.5 3.43594 10.5 4.09063 10.5 5.4V6.5C10.5 8.38562 10.5 9.32843 9.91421 9.91421C9.32843 10.5 8.38562 10.5 6.5 10.5H5.4C4.09063 10.5 3.43594 10.5 2.955 10.2053C2.68589 10.0404 2.45963 9.81411 2.29472 9.545C2 9.06406 2 8.40937 2 7.1Z" stroke="currentColor" strokeWidth="1.5" />
    <path d="M5 6.25C5 5.73459 5 5.47689 5.12911 5.29493C5.17466 5.23072 5.23072 5.17466 5.29493 5.12911C5.47689 5 5.73459 5 6.25 5C6.76541 5 7.02311 5 7.20507 5.12911C7.26928 5.17466 7.32534 5.23072 7.37089 5.29493C7.5 5.47689 7.5 5.73459 7.5 6.25C7.5 6.76541 7.5 7.02311 7.37089 7.20507C7.32534 7.26928 7.26928 7.5 7.20507 7.5C7.02311 7.5 6.76541 7.5 6.25 7.5C5.73459 7.5 5.47689 7.5 5.29493 7.37089C5.23072 7.32534 5.17466 7.26928 5.12911 7.20507C5 7.02311 5 6.76541 5 6.25Z" fill="currentColor" />
    <path d="M5 17.75C5 17.2346 5 16.9769 5.12911 16.7949C5.17466 16.7307 5.23072 16.6747 5.29493 16.6291C5.47689 16.5 5.73459 16.5 6.25 16.5C6.76541 16.5 7.02311 16.5 7.20507 16.6291C7.26928 16.6747 7.32534 16.7307 7.37089 16.7949C7.5 16.9769 7.5 17.2346 7.5 17.75C7.5 18.2654 7.5 18.5231 7.37089 18.7051C7.32534 18.7693 7.26928 18.8253 7.20507 18.8709C7.02311 19 6.76541 19 6.25 19C5.73459 19 5.47689 19 5.29493 18.8709C5.23072 18.8253 5.17466 18.7693 5.12911 18.7051C5 18.5231 5 18.2654 5 17.75Z" fill="currentColor" />
    <path d="M16 17.75C16 17.0478 16 16.6967 16.1685 16.4444C16.2415 16.3352 16.3352 16.2415 16.4444 16.1685C16.6967 16 17.0478 16 17.75 16C18.4522 16 18.8033 16 19.0556 16.1685C19.1648 16.2415 19.2585 16.3352 19.3315 16.4444C19.5 16.6967 19.5 17.0478 19.5 17.75C19.5 18.4522 19.5 18.8033 19.3315 19.0556C19.2585 19.1648 19.1648 19.2585 19.0556 19.3315C18.8033 19.5 18.4522 19.5 17.75 19.5C17.0478 19.5 16.6967 19.5 16.4444 19.3315C16.3352 19.2585 16.2415 19.1648 16.1685 19.0556C16 18.8033 16 18.4522 16 17.75Z" fill="currentColor" />
  </svg>
}

function normalizedWorldShareBase(shareWebsite: string | undefined): string {
  const raw = shareWebsite?.trim() || 'https://jiwo.cc'
  try {
    const url = new URL(raw)
    return `${url.protocol}//${url.host}`
  } catch {
    return 'https://jiwo.cc'
  }
}

export function buildProfileShareUrl(shareWebsite: string | undefined, arkmeId: string): string {
  const id = arkmeId.trim()
  const base = normalizedWorldShareBase(shareWebsite)
  return id === '' ? base : `${base}/${encodeURIComponent(id)}`
}

export function profileQrDataUrlFromShareUrl(shareUrl: string): string {
  const code = qrcode(0, 'M')
  code.addData(shareUrl)
  code.make()
  return code.createDataURL(5, 8)
}

function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText !== undefined) return navigator.clipboard.writeText(value)
  return Promise.reject(new Error('clipboard unavailable'))
}

function localArkmeIdValidation(text: string, minLength: number): string | undefined {
  const value = text.trim()
  if (value === '') return undefined
  if (!/^[A-Za-z]/.test(value)) return tr("即我号不能以数字或符号开头")
  if (!/^[A-Za-z0-9_-]+$/.test(value)) return tr("即我号仅支持字母、数字、下划线或减号")
  const length = [...value].length
  if (length < minLength || length > 20) return minLength === 5 ? tr("即我号需要5-20个字符") : tr("即我号需要6-20个字符")
  return undefined
}

function arkmeIdAvailabilityText(availability: ArkmeIdAvailabilitySnapshot | undefined): string {
  if (availability === undefined) return ''
  if (availability.available) return tr("✅ 当前即我号可设置")
  switch (availability.reason) {
    case 'modify_limited': return '❌ 即我号仅可设置一次'
    case 'invalid': return '❌ 即我号格式不正确'
    case 'taken': return '❌ 该即我号已被占用'
    default: return tr("服务器繁忙，请稍后重试")
  }
}

function SettingsDialog({
  title,
  children,
  onClose,
}: {
  title: string
  children: ReactNode
  onClose: () => void
}) {
  return <div className="arkme-account-dialog-backdrop" role="presentation" onClick={onClose}>
    <section className="arkme-account-dialog" role="dialog" aria-modal="true" aria-label={title} onClick={event => { event.stopPropagation() }}>
      <header>
        <h3>{title}</h3>
        <button data-arkme-feedback="neutral" type="button" aria-label={tr("关闭")} onClick={onClose}><X size={20} aria-hidden /></button>
      </header>
      {children}
    </section>
  </div>
}

function ProfileQrDialog({
  profile,
  shareWebsite,
  onClose,
}: {
  profile: ArkmeUserProfile
  shareWebsite: string | undefined
  onClose: () => void
}) {
  useArkmeLocale()
  const shareUrl = buildProfileShareUrl(shareWebsite, profile.arkmeId)
  const [status, setStatus] = useState('')
  const qrDataUrl = useMemo(() => profileQrDataUrlFromShareUrl(shareUrl), [shareUrl])
  return <SettingsDialog title={tr("我的二维码")} onClose={onClose}>
    <div className="arkme-account-qr-card">
      <ArkmeUserAvatar {...(profile.avatarRef ? { avatarRef: profile.avatarRef } : {})} size={52} label={tr("当前用户头像")} />
      <strong>{profile.displayName || tr("即我用户")}</strong>
      <span>{tr("即我号：")}{profile.arkmeId || '-'}</span>
      <img src={qrDataUrl} alt={tr("我的即我二维码")} />
      <small>{tr("扫码查看我的主页")}</small>
    </div>
    <div className="arkme-account-dialog-actions">
      <button data-arkme-feedback="neutral" type="button" onClick={() => {
        void copyText(shareUrl).then(() => { setStatus('主页链接已复制') }).catch(() => { setStatus('复制失败，请稍后重试') })
      }}><Copy size={16} aria-hidden />{tr("复制主页链接")}</button>
    </div>
    {status !== '' ? <p className="arkme-account-dialog-status" role="status">{status}</p> : null}
  </SettingsDialog>
}

function ArkmeIdDialog({
  profile,
  onClose,
  onUpdated,
}: {
  profile: ArkmeUserProfile
  onClose: () => void
  onUpdated: (snapshot: ArkmeUserProfileSnapshot) => void
}) {
  useArkmeLocale()
  const minLength = profile.accountType === 2 ? 5 : 6
  const [value, setValue] = useState(profile.arkmeId)
  const [availability, setAvailability] = useState<ArkmeIdAvailabilitySnapshot>()
  const [checking, setChecking] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const normalized = value.trim()
  const localError = localArkmeIdValidation(value, minLength)
  const canSubmit = !submitting && localError === undefined
    && (normalized === profile.arkmeId || availability?.available === true)

  useEffect(() => {
    setError('')
    setAvailability(undefined)
    if (normalized === '' || normalized === profile.arkmeId || localError !== undefined) {
      setChecking(false)
      return
    }
    let active = true
    setChecking(true)
    const timer = window.setTimeout(() => {
      void callArkme<ArkmeIdAvailabilitySnapshot>('user.arkme-id.check', { arkmeId: normalized })
        .then(result => { if (active) setAvailability(result) })
        .catch(caught => {
          if (active) setError(caught instanceof Error ? caught.message : String(caught))
        })
        .finally(() => { if (active) setChecking(false) })
    }, 600)
    return () => {
      active = false
      window.clearTimeout(timer)
    }
  }, [localError, normalized, profile.arkmeId])

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    if (normalized === profile.arkmeId) {
      onClose()
      return
    }
    if (!window.confirm('即我号仅可设置一次，创建成功后不可修改，是否继续？')) return
    setSubmitting(true)
    setError('')
    try {
      await callArkme<ArkmeIdMutationResult>('user.arkme-id.set', { arkmeId: normalized })
      const snapshot = await callArkme<ArkmeUserProfileSnapshot>('user.profile.refresh')
      onUpdated(snapshot)
      onClose()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught))
    } finally {
      setSubmitting(false)
    }
  }

  const statusText = localError ?? (checking ? '加载中…' : arkmeIdAvailabilityText(availability))
  return <SettingsDialog title={tr("设置即我号")} onClose={onClose}>
    <form className="arkme-account-form" onSubmit={event => { void submit(event) }}>
      <label>
        <span>{tr("即我号")}</span>
        <input value={value} maxLength={20} autoFocus placeholder={tr("请输入即我号")} onChange={event => { setValue(event.target.value) }} />
      </label>
      <p className="arkme-account-rule">{tr("需字母开头 · 最少")}{minLength}{tr("位 · 暂仅可设置一次")}</p>
      {statusText !== '' ? <p className={`arkme-account-dialog-status${localError !== undefined || availability?.available === false || error !== '' ? ' is-error' : ''}`} role="status">{statusText}</p> : null}
      {error !== '' ? <p className="arkme-account-dialog-status is-error" role="alert">{error}</p> : null}
      <div className="arkme-account-dialog-actions">
        <button data-arkme-feedback="neutral" type="button" onClick={onClose}>{tr("取消")}</button>
        <button data-arkme-feedback="primary" type="submit" disabled={!canSubmit}>{submitting ? <CircleNotch className="arkme-icon-spin" size={16} aria-hidden /> : null}{tr("确认创建")}</button>
      </div>
    </form>
  </SettingsDialog>
}

function PhoneBindDialog({
  config,
  profile,
  onClose,
  onUpdated,
}: {
  config: ArkmeClientConfig | undefined
  profile: ArkmeUserProfile
  onClose: () => void
  onUpdated: (snapshot: ArkmeUserProfileSnapshot, auth: ArkmeAuthSnapshot) => void
}) {
  useArkmeLocale()
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [phoneBusy, setPhoneBusy] = useState(false)
  const [status, setStatus] = useState('')
  const [countdown, setCountdown] = useState(0)
  const phoneValid = /^1[3-9][0-9]{9}$/.test(phone.replace(/[\s-]/g, ''))
  const codeValid = /^[0-9]{6}$/.test(code.trim())
  const actionLabel = profile.contact.phoneMasked === undefined ? '绑定手机号' : '更换手机号'

  useEffect(() => {
    if (countdown <= 0) return
    const timer = window.setTimeout(() => { setCountdown(countdown - 1) }, 1000)
    return () => { window.clearTimeout(timer) }
  }, [countdown])

  const sendCode = async () => {
    if (!phoneValid) {
      setStatus('请输入正确的手机号')
      return
    }
    setPhoneBusy(true)
    setStatus('')
    try {
      const captcha = await verifyPhoneCaptcha(config?.captchaId ?? '', phone)
      await callArkme('auth.phone.send', { phone, captcha })
      setCountdown(60)
      setStatus('已发送验证码')
    } catch (caught) {
      setStatus(caught instanceof Error ? caught.message : String(caught))
    } finally {
      setPhoneBusy(false)
    }
  }

  const verify = async (event: FormEvent) => {
    event.preventDefault()
    if (!phoneValid || !codeValid) {
      setStatus('请输入正确的手机号和验证码')
      return
    }
    setPhoneBusy(true)
    setStatus('')
    try {
      const auth = await callArkme<ArkmeAuthSnapshot>('auth.phone.verify', { phone, code })
      arkmeAuthStore.setAuth(auth)
      const snapshot = await callArkme<ArkmeUserProfileSnapshot>('user.profile.refresh')
      onUpdated(snapshot, auth)
      onClose()
    } catch (caught) {
      setStatus(caught instanceof Error ? caught.message : String(caught))
    } finally {
      setPhoneBusy(false)
    }
  }

  return <SettingsDialog title={actionLabel} onClose={onClose}>
    <form className="arkme-account-form" onSubmit={event => { void verify(event) }}>
      {profile.contact.phoneMasked !== undefined ? <p className="arkme-account-rule">{tr("当前绑定的手机号码为")} {profile.contact.phoneMasked}</p> : null}
      <label>
        <span>{tr("手机号")}</span>
        <input value={phone} autoFocus inputMode="tel" placeholder={tr("请输入手机号")} onChange={event => { setPhone(event.target.value) }} />
      </label>
      <label>
        <span>{tr("验证码")}</span>
        <span className="arkme-account-code-row">
          <input value={code} inputMode="numeric" maxLength={6} placeholder={tr("请输入验证码")} onChange={event => { setCode(event.target.value) }} />
          <button data-arkme-feedback="neutral" type="button" disabled={phoneBusy || !phoneValid || countdown > 0} onClick={() => { void sendCode() }}>
            {countdown > 0 ? `${countdown}s` : '发送验证码'}
          </button>
        </span>
      </label>
      {status !== '' ? <p className={`arkme-account-dialog-status${status.includes('失败') || status.includes('错误') || status.includes('请输入') || status.includes('已绑定') ? ' is-error' : ''}`} role="status">{status}</p> : null}
      <div className="arkme-account-dialog-actions">
        <button data-arkme-feedback="neutral" type="button" onClick={onClose}>{tr("取消")}</button>
        <button data-arkme-feedback="primary" type="submit" disabled={phoneBusy || !phoneValid || !codeValid}>{phoneBusy ? <CircleNotch className="arkme-icon-spin" size={16} aria-hidden /> : null}{profile.contact.phoneMasked === undefined ? tr("绑 定") : tr("更 换")}</button>
      </div>
    </form>
  </SettingsDialog>
}

type OverflowYReader = (element: HTMLElement) => string

function browserOverflowY(element: HTMLElement): string {
  return typeof window === 'undefined' ? '' : window.getComputedStyle(element).overflowY
}

export function scrollArkmeSettingsSurface(
  surface: HTMLElement | null,
  readOverflowY: OverflowYReader = browserOverflowY,
): void {
  if (surface === null) return
  let scrollOwner = surface.parentElement
  while (scrollOwner !== null && !['auto', 'scroll', 'overlay'].includes(readOverflowY(scrollOwner))) {
    scrollOwner = scrollOwner.parentElement
  }
  const target = scrollOwner ?? surface
  target.scrollTop = 0
}

export interface ArkmeAppUpdateRow {
  label: string
  current: string
  latest: string
  feedback?: string
}

function versionLabel(version: string | undefined): string {
  return `v${version?.trim() || '…'}`
}

interface ArkmeDesktopScope {
  readonly arkmeDesktop?: Readonly<{
    appVersion?: string
    harnessVersion?: string
  }>
}

export function aboutArkmeVersion(
  currentVersion: string | undefined,
  scope: ArkmeDesktopScope = globalThis as unknown as ArkmeDesktopScope,
): string {
  return versionLabel(scope.arkmeDesktop?.appVersion ?? currentVersion)
}

export function aboutHarnessVersion(
  scope: ArkmeDesktopScope = globalThis as unknown as ArkmeDesktopScope,
): string {
  return versionLabel(scope.arkmeDesktop?.harnessVersion)
}

export function updateVersionText(current: string, latest: string): string {
  if (current === 'v…') return tr("当前版本读取中…")
  if (latest === 'v…') return `当前 ${current}`
  return current === latest ? `当前 ${current} · 已是最新版本` : `当前 ${current} → 最新 ${latest}`
}

export function buildArkmeAppUpdateRow(input: {
  app?: Pick<ArkmeAppUpdateSnapshot, 'status' | 'currentVersion' | 'noUpdateAvailable' | 'latestVersion' | 'error' | 'failureStage' | 'downloadedBytes' | 'totalBytes'>
  appError?: string
}): ArkmeAppUpdateRow {
  const status = input.app?.status
  const latest = versionLabel(input.app?.latestVersion ?? input.app?.currentVersion)
  const downloaded = input.app?.downloadedBytes ?? 0
  const total = input.app?.totalBytes
  const progress = total !== undefined && total > 0
    ? `${Math.max(0, Math.min(100, Math.round(downloaded / total * 100)))}%`
    : undefined
  const feedback = input.appError?.trim()
    ? input.appError.trim()
    : status === 'checking'
      ? '正在检查更新…'
      : status === 'current'
        ? input.app?.noUpdateAvailable === true ? '暂无可用版本' : '当前已是最新版本'
        : status === 'available'
          ? `发现新版本 ${latest}`
          : status === 'downloading'
            ? `正在下载更新${progress === undefined ? '' : ` · ${progress}`}`
            : status === 'downloaded'
              ? '更新已下载，前往 APP 更新继续'
              : status === 'installing'
                ? '正在安装更新…'
              : status === 'failed'
                ? `${input.app?.failureStage === 'download' ? '下载' : input.app?.failureStage === 'install' ? '安装' : '检查'}失败：${input.app?.error || '请稍后重试'}`
                : status === 'idle' ? '等待检查更新' : '正在读取更新状态…'
  return {
    label: 'APP',
    current: versionLabel(input.app?.currentVersion),
    latest,
    ...(feedback === undefined ? {} : { feedback }),
  }
}

export function arkmeNotificationPermissionLabel(permission: ArkmeDesktopNotificationPermission): string {
  return permission === 'granted'
    ? tr("已开启")
    : permission === 'denied'
      ? tr("系统通知未开启，点击前往设置")
      : permission === 'default'
        ? tr("尚未授权，点击开启")
        : permission === 'system-managed' ? tr("由系统管理") : tr("不可用")
}

export function ArkmeSettingsSurface({ view = 'account' }: { view?: 'account' | 'general' | 'about' } = {}) {
  useArkmeLocale()
  const surfaceRef = useRef<HTMLDivElement>(null)
  const authState = useSyncExternalStore(arkmeAuthStore.subscribe, arkmeAuthStore.getSnapshot, arkmeAuthStore.getSnapshot)
  const appUpdateState = useSyncExternalStore(arkmeAppUpdateStore.subscribe, arkmeAppUpdateStore.getSnapshot, arkmeAppUpdateStore.getSnapshot)
  const [profile, setProfile] = useState<ArkmeUserProfile>()
  const [clientConfig, setClientConfig] = useState<ArkmeClientConfig>()
  const [logoutBusy, setLogoutBusy] = useState(false)
  const [notificationBusy, setNotificationBusy] = useState(false)
  const [error, setError] = useState('')
  const [accountFeedback, setAccountFeedback] = useState('')
  const [activeAccountDialog, setActiveAccountDialog] = useState<'qr' | 'arkme-id' | 'phone' | null>(null)
  const notificationPermission = useSyncExternalStore(
    arkmeDesktopNotifications.subscribePermission,
    arkmeDesktopNotifications.getPermissionSnapshot,
    arkmeDesktopNotifications.getPermissionSnapshot,
  ).permission
  const [backgroundSoundBusy, setBackgroundSoundBusy] = useState(false)
  const backgroundSoundBusyRef = useRef(false)
  const backgroundSoundAccountGenerationRef = useRef(0)
  const [backgroundSoundCapabilityState, setBackgroundSoundCapabilityState] = useState<{
    accountId?: string
    status: ArkmeBackgroundSoundCapabilityStatus
  }>({ status: 'loading' })
  const [backgroundSoundEligibilityState, setBackgroundSoundEligibilityState] = useState<{
    accountId?: string
    status: ArkmeBackgroundSoundEligibilityStatus
  }>({ status: 'loading' })
  const [backgroundSoundPermission, setBackgroundSoundPermission] = useState<ArkmeMicrophonePermissionState>('prompt')
  const [backgroundSoundFeedback, setBackgroundSoundFeedback] = useState('')

  const applyProfileSnapshot = useCallback((snapshot: ArkmeUserProfileSnapshot) => {
    if (snapshot.profile !== null) setProfile(snapshot.profile)
  }, [])

  const loadProfile = useCallback((signal?: AbortSignal) => {
    if (authState.auth?.status !== 'authenticated') {
      setProfile(undefined)
      setError('')
      return
    }
    setProfile(undefined)
    setError('')
    void callArkme<ArkmeUserProfileSnapshot>('user.profile', undefined, signal)
      .then(async snapshot => {
        if (signal?.aborted) return snapshot
        if (snapshot.profile !== null) applyProfileSnapshot(snapshot)
        return await callArkme<ArkmeUserProfileSnapshot>('user.profile.refresh', undefined, signal)
      })
      .then(snapshot => { if (!signal?.aborted) applyProfileSnapshot(snapshot) })
      .catch(caught => {
        if (!signal?.aborted) setError(caught instanceof Error ? caught.message : String(caught))
      })
  }, [applyProfileSnapshot, authState.auth])

  useEffect(() => {
    const controller = new AbortController()
    void callArkme<ArkmeClientConfig>('auth.config', undefined, controller.signal)
      .then(config => { if (!controller.signal.aborted) setClientConfig(config) })
      .catch(() => undefined)
    return () => { controller.abort() }
  }, [])

  useEffect(() => {
    if (view !== 'account') {
      setError('')
      return
    }
    const controller = new AbortController()
    loadProfile(controller.signal)
    return () => { controller.abort() }
  }, [loadProfile, view])

  useEffect(() => {
    const refresh = () => { void arkmeDesktopNotifications.refreshPermission() }
    refresh()
    if (typeof window === 'undefined') return
    window.addEventListener('focus', refresh)
    return () => { window.removeEventListener('focus', refresh) }
  }, [])

  useLayoutEffect(() => {
    scrollArkmeSettingsSurface(surfaceRef.current)
  }, [])

  const logout = async () => {
    setLogoutBusy(true)
    setError('')
    try {
      const snapshot = await callArkme<ArkmeAuthSnapshot>('auth.logout')
      arkmeAuthStore.setAuth(snapshot)
      clearLastNavigationCache()
      arkmeUi.authChanged(false)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught))
    } finally {
      setLogoutBusy(false)
    }
  }

  const enableNotifications = async () => {
    setNotificationBusy(true)
    try {
      await arkmeDesktopNotifications.requestPermission()
    } finally {
      setNotificationBusy(false)
    }
  }

  const openNotificationSettings = async () => {
    setNotificationBusy(true)
    setError('')
    try {
      if (!await arkmeDesktopNotifications.openPermissionSettings()) setError('无法打开系统通知设置')
    } finally {
      setNotificationBusy(false)
    }
  }

  const authenticated = authState.auth?.status === 'authenticated'
  const authenticatedUserId = authenticated ? authState.auth?.userId : undefined
  const authenticatedAccountKey = authState.auth?.status === 'authenticated'
    ? `${authState.auth.environment}:${String(authState.auth.userId)}`
    : undefined
  const subscribeBackgroundSound = useCallback(
    (listener: () => void) => subscribeArkmeBackgroundSoundCapturePreference(authenticatedAccountKey, listener),
    [authenticatedAccountKey],
  )
  const backgroundSoundEnabled = useSyncExternalStore(
    subscribeBackgroundSound,
    () => arkmeBackgroundSoundCaptureEnabled(authenticatedAccountKey),
    () => false,
  )
  const backgroundSoundCapability = backgroundSoundCapabilityState.accountId === authenticatedAccountKey
    ? backgroundSoundCapabilityState.status
    : 'loading'
  const backgroundSoundEligibility = backgroundSoundEligibilityState.accountId === authenticatedAccountKey
    ? backgroundSoundEligibilityState.status
    : 'loading'
  useEffect(() => {
    const generation = backgroundSoundAccountGenerationRef.current + 1
    backgroundSoundAccountGenerationRef.current = generation
    backgroundSoundBusyRef.current = false
    setBackgroundSoundBusy(false)
    setBackgroundSoundFeedback('')
    if (authenticatedUserId === undefined || authenticatedAccountKey === undefined) {
      setBackgroundSoundCapabilityState({ status: 'loading' })
      setBackgroundSoundEligibilityState({ status: 'loading' })
      setBackgroundSoundPermission('prompt')
      setBackgroundSoundFeedback('')
      return
    }
    const targetUserId = authenticatedUserId
    const targetAccountKey = authenticatedAccountKey
    let active = true
    let controller: AbortController | undefined
    const refresh = () => {
      const readToken = arkmeBackgroundSoundServerPreferenceRuntime.beginRead(targetAccountKey)
      if (!arkmeBackgroundSoundServerPreferenceRuntime.current(readToken)) return
      if (backgroundSoundBusyRef.current) {
        backgroundSoundBusyRef.current = false
        setBackgroundSoundBusy(false)
        setBackgroundSoundFeedback('')
      }
      controller?.abort()
      const requestController = new AbortController()
      controller = requestController
      const sameAccount = () => {
        const current = arkmeAuthStore.getSnapshot().auth
        return active
          && !requestController.signal.aborted
          && backgroundSoundAccountGenerationRef.current === generation
          && arkmeBackgroundSoundServerPreferenceRuntime.current(readToken)
          && current?.status === 'authenticated'
          && `${current.environment}:${String(current.userId)}` === targetAccountKey
      }
      void Promise.all([
        retryArkmeRead(
          () => callArkme<ArkmeProviderCapabilities>('provider.capabilities', {}, requestController.signal),
          { signal: requestController.signal, retryDelays: [250, 750] },
        ).catch(() => undefined),
        retryArkmeRead(
          () => callArkme<ArkmeBackgroundSoundPreference>('settings.background-sound.get', undefined, requestController.signal),
          { signal: requestController.signal, retryDelays: [250, 750] },
        ).catch(() => undefined),
        arkmeMicrophonePermissionState(),
      ]).then(([capabilities, preference, permission]) => {
        if (!sameAccount()) return
        setBackgroundSoundPermission(permission)
        if (capabilities === undefined) {
          setBackgroundSoundCapabilityState({ accountId: targetAccountKey, status: 'loading' })
          setBackgroundSoundEligibilityState({ accountId: targetAccountKey, status: 'loading' })
          return
        }
        const supported = capabilities.features.backgroundSound === true
        setBackgroundSoundCapabilityState({ accountId: targetAccountKey, status: supported ? 'supported' : 'unsupported' })
        if (!supported) return
        if (preference === undefined) {
          setBackgroundSoundEligibilityState({ accountId: targetAccountKey, status: 'loading' })
          return
        }
        const eligibility = resolveArkmeBackgroundSoundEligibility(preference)
        setBackgroundSoundEligibilityState({ accountId: targetAccountKey, status: eligibility })
        applyArkmeBackgroundSoundOwnerSnapshot(targetAccountKey, preference, undefined, targetUserId)
      })
    }
    const unsubscribeRuntime = arkmeBackgroundSoundServerPreferenceRuntime.subscribe(targetAccountKey, refresh)
    refresh()
    return () => { active = false; controller?.abort(); unsubscribeRuntime() }
  }, [authenticatedAccountKey, authenticatedUserId])
  const bindingRequired = authState.auth?.status === 'binding-required'
  const displayName = authenticated
    ? profile?.displayName.trim() || profile?.nickname.trim() || '我的账户'
    : bindingRequired ? '待完成登录' : authState.checked ? '当前未登录' : '我的账户'
  const accountDescription = authenticated
    ? profile?.arkmeId ? `即我号 ${profile.arkmeId}` : '即我号读取中…'
    : bindingRequired ? '请完成手机号绑定' : authState.checked ? '登录后显示账户信息' : '正在读取账户状态…'
  const notificationLabel = arkmeNotificationPermissionLabel(notificationPermission)
  const backgroundSoundBaseDescription = describeArkmeBackgroundSoundSetting({
    capability: backgroundSoundCapability,
    eligibility: backgroundSoundEligibility,
    enabled: backgroundSoundEnabled,
    permission: backgroundSoundPermission,
  })
  const backgroundSoundDescription = backgroundSoundFeedback === ''
    ? backgroundSoundBaseDescription
    : `${backgroundSoundFeedback} · ${backgroundSoundBaseDescription}`
  const appUpdateRow = buildArkmeAppUpdateRow({
    ...(appUpdateState.status === undefined ? {} : { app: appUpdateState.status }),
    ...(appUpdateState.error === '' ? {} : { appError: appUpdateState.error }),
  })
  const currentArkmeId = profile?.arkmeId.trim() ?? ''
  const phoneMasked = profile?.contact.phoneMasked
  const changeBackgroundSound = async (enabled: boolean) => {
    if (authenticatedUserId === undefined || authenticatedAccountKey === undefined || backgroundSoundCapability !== 'supported'
      || backgroundSoundEligibility !== 'eligible' || backgroundSoundBusyRef.current) return
    const targetUserId = authenticatedUserId
    const targetAccountKey = authenticatedAccountKey
    const generation = backgroundSoundAccountGenerationRef.current
    const mutationToken = arkmeBackgroundSoundServerPreferenceRuntime.beginMutation(targetAccountKey)
    const sameAccount = () => {
      const current = arkmeAuthStore.getSnapshot().auth
      return backgroundSoundAccountGenerationRef.current === generation
        && arkmeBackgroundSoundServerPreferenceRuntime.current(mutationToken)
        && current?.status === 'authenticated'
        && `${current.environment}:${String(current.userId)}` === targetAccountKey
    }
    backgroundSoundBusyRef.current = true
    setBackgroundSoundBusy(true)
    setBackgroundSoundFeedback(enabled ? '正在开启文字背景音…' : '')
    try {
      let persistedEnabled = enabled
      await setArkmeBackgroundSoundFromSettings(
        targetAccountKey,
        enabled,
        async next => {
          if (!sameAccount()) throw new Error('账号已切换，本次背景音设置未保存')
          const preference = await callArkme<ArkmeBackgroundSoundPreference>('settings.background-sound.update', {
            enabled: next,
            expectedUserId: targetUserId,
          })
          if (!sameAccount()) throw new Error('账号已切换，本次背景音设置结果已丢弃')
          const eligibility = resolveArkmeBackgroundSoundEligibility(preference)
          setBackgroundSoundEligibilityState({ accountId: targetAccountKey, status: eligibility })
          persistedEnabled = preference.eligible ? preference.enabled : false
          applyArkmeBackgroundSoundOwnerSnapshot(targetAccountKey, preference, undefined, targetUserId)
        },
        undefined,
        sameAccount,
      )
      if (!sameAccount()) return
      setBackgroundSoundFeedback(persistedEnabled === enabled
        ? enabled ? '文字背景音已开启' : '文字背景音已关闭'
        : '当前账号暂不支持开启文字背景音')
    } catch (caught) {
      if (sameAccount()) setBackgroundSoundFeedback(caught instanceof Error ? caught.message : String(caught))
    } finally {
      const permission = await arkmeMicrophonePermissionState()
      if (sameAccount()) {
        setBackgroundSoundPermission(permission)
        backgroundSoundBusyRef.current = false
        setBackgroundSoundBusy(false)
      }
      arkmeBackgroundSoundServerPreferenceRuntime.finish(mutationToken)
    }
  }

  return <div ref={surfaceRef} className="arkme-redesign-settings-surface" data-arkme-settings-surface data-arkme-settings-view={view} aria-label={tr("Arkme 设置")}>
    <div className="arkme-redesign-settings-shell">
      {view === 'account' && <>
      {!authenticated && <div className="arkme-redesign-settings-profile">
        <ArkmeUserAvatar {...(profile?.avatarRef ? { avatarRef: profile.avatarRef } : {})} size={56} label={tr("当前用户头像")} />
        <div>
          <h1>{displayName}</h1>
          {!authenticated && <p><span>{accountDescription}</span></p>}
        </div>
      </div>}
      {authenticated && profile && authenticatedAccountKey && <ArkmeProfileEditor key={authenticatedAccountKey} profile={profile} accountScope={authenticatedAccountKey} onUpdated={applyProfileSnapshot} />}

      {!authenticated && <SettingsGroup title={tr("账户")}>
        <SettingsRow
          title={bindingRequired ? tr("待完成登录") : authState.checked ? tr("当前未登录") : tr("正在读取登录状态…")}
          description={bindingRequired ? tr("完成手机号绑定后即可登录") : tr("登录 Arkme 后可管理账户")}
        />
      </SettingsGroup>}

      {authenticated && <SettingsGroup title={tr("账号信息")}>
        <div className="arkme-account-id-with-qr">
        <AccountInfoRow
          icon={<IdentificationCard size={18} />}
          title={tr("即我号")}
          value={currentArkmeId === '' ? tr('暂未获取到即我号') : currentArkmeId}
          action={tr(profile?.canUpdateArkmeId === false ? '不可修改' : '修改')}
          disabled={profile === undefined || profile.canUpdateArkmeId === false}
          onClick={profile !== undefined && profile.canUpdateArkmeId !== false ? () => { setActiveAccountDialog('arkme-id'); setAccountFeedback('') } : undefined}
        />
        <button type="button" className="arkme-account-profile-qr" aria-label={tr('查看我的二维码')} disabled={!profile || !currentArkmeId} onClick={() => { setActiveAccountDialog('qr') }}><WorldShareQrIcon /></button>
        </div>
        <AccountInfoRow
          icon={<Phone size={18} />}
          title={tr("手机号")}
          value={phoneMasked ?? tr('未绑定')}
          action={tr(phoneMasked === undefined ? '绑定' : '换绑')}
          disabled={profile === undefined}
          onClick={profile !== undefined ? () => { setActiveAccountDialog('phone'); setAccountFeedback('') } : undefined}
        />
        {/* DSH currently has no WeChat authorization adapter. Never substitute an explanatory dialog. */}
        <WechatBindingSettingsRow bound={profile === undefined ? undefined : profile.bindings?.wechat === true} nickname={profile?.bindingNames?.wechat} />
        {/* Binding metadata is distinct from the Arkme profile. Do not show the Arkme
            avatar as a provider avatar when the profile API does not supply one. */}
        {profile?.bindings?.apple && <AccountInfoRow icon={<AppleLogo size={18} />} title="Apple ID" value={profile.bindingNames?.apple || tr('已绑定')} />}
        {profile?.bindings?.google && <AccountInfoRow icon={<GoogleLogo size={18} />} title="Google" value={profile.bindingNames?.google || tr('已绑定')} />}
        {profile?.bindings?.huawei && <AccountInfoRow icon={<DeviceMobile size={18} />} title={tr("华为 ID")} value={tr('已绑定')} />}
        {profile?.contact.emailMasked && <AccountInfoRow icon={<EnvelopeSimple size={18} />} title={tr("邮箱")} value={profile.contact.emailMasked} />}
        {accountFeedback !== '' ? <p className="arkme-account-feedback" role="status"><WarningCircle size={14} aria-hidden />{accountFeedback}</p> : null}
      </SettingsGroup>}
      {authenticated && <div className="arkme-account-logout">
        <button data-arkme-feedback="danger" type="button" disabled={logoutBusy} onClick={() => { void logout() }}>{logoutBusy ? tr("正在退出…") : tr("退出登录")}</button>
      </div>}
      </>}

      {view === 'general' && <>
      <SettingsGroup title={tr("通用")}>
        <SettingsRow
          title={tr("通知")}
          description={notificationLabel}
          disabled={notificationBusy}
          {...(notificationPermission === 'default'
            ? { onClick: () => { void enableNotifications() } }
            : notificationPermission === 'denied'
              ? { onClick: () => { void openNotificationSettings() } }
              : {})}
        />
      </SettingsGroup>

      {authenticated && authenticatedUserId !== undefined ? <SettingsGroup title={tr("隐私与权限")}>
        <BackgroundSoundSettingsRow
          checked={backgroundSoundCapability === 'supported' && backgroundSoundEligibility === 'eligible' && backgroundSoundEnabled}
          busy={backgroundSoundBusy || backgroundSoundCapability === 'loading' || backgroundSoundEligibility === 'loading'}
          disabled={backgroundSoundCapability !== 'supported' || backgroundSoundEligibility !== 'eligible'
            || !backgroundSoundEnabled && backgroundSoundPermission === 'unavailable'}
          description={backgroundSoundDescription}
          onChange={enabled => { void changeBackgroundSound(enabled) }}
        />
      </SettingsGroup> : null}
      </>}

      {view === 'about' && <>
      <ArkmeAboutProduct />
      <SettingsGroup id="arkme-settings-about">
        <VersionSettingsRow
          title={tr("ArkME 客户端")}
          version={aboutArkmeVersion(appUpdateState.status?.currentVersion)}
          feedback={`${updateVersionText(appUpdateRow.current, appUpdateRow.latest)} · ${tr(appUpdateRow.feedback ?? '正在读取更新状态…')}`}
          actionLabel={tr("打开 APP 更新")}
          onAction={() => { void arkmeAppUpdateStore.open() }}
        />
        <VersionSettingsRow title={tr("ArkME 插件")} version={`v${pluginManifest.version}`} />
        <VersionSettingsRow title="DeepSeek Harness" version={aboutHarnessVersion()} />
        <SettingsRow title={tr("用户协议")} description={tr("查看 Arkme 用户协议")} href="https://www.arkme.ai/article/user-aggrement-v1.html" />
        <SettingsRow title={tr("隐私条款")} description={tr("查看 Arkme 隐私条款")} href="https://www.arkme.ai/article/privacy-aggrement-v1.html" />
      </SettingsGroup>
      <ArkmeAboutDetails accountScope={authenticatedAccountKey} />
      </>}

      {error !== '' && <div className="arkme-redesign-settings-error" role="alert">{error}</div>}
    </div>
    {activeAccountDialog === 'qr' && profile !== undefined
      ? <ProfileQrDialog profile={profile} shareWebsite={clientConfig?.shareWebsite} onClose={() => { setActiveAccountDialog(null) }} />
      : null}
    {activeAccountDialog === 'arkme-id' && profile !== undefined
      ? <ArkmeIdDialog profile={profile} onClose={() => { setActiveAccountDialog(null) }} onUpdated={applyProfileSnapshot} />
      : null}
    {activeAccountDialog === 'phone' && profile !== undefined
      ? <PhoneBindDialog
          config={clientConfig}
          profile={profile}
          onClose={() => { setActiveAccountDialog(null) }}
          onUpdated={(snapshot) => { applyProfileSnapshot(snapshot); arkmeUi.authChanged(true) }}
        />
      : null}
  </div>
}
